import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FormControl, MenuItem, Select } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import { useAuth } from "../../contexts/auth";
import { useOrganizationList } from "../../hooks/useOrganizationList";
import { isSuperAdmin } from "../../utils/orgSelection";
import { colors } from "../../pages";

function displayName(org) {
  return org.org_name || `Organization ${org.org_id}`;
}

function OrganizationSelectForSuperAdmin() {
  const navigate = useNavigate();
  const { selectedOrg, selectOrganization } = useAuth();
  const { organizations } = useOrganizationList();

  // The selection must stay visible while the list loads, and when the list
  // does not contain it.
  const options = useMemo(() => {
    if (!selectedOrg) return organizations;
    const listed = organizations.some(
      (org) => org.org_id === selectedOrg.org_id
    );
    return listed ? organizations : [selectedOrg, ...organizations];
  }, [organizations, selectedOrg]);

  const handleChange = useCallback(
    (event) => {
      const next = options.find((org) => org.org_id === event.target.value);
      if (!next || next.org_id === selectedOrg?.org_id) return;

      selectOrganization(next);
      // Leave the current page: it holds data of the previous organization.
      navigate("/");
    },
    [navigate, options, selectOrganization, selectedOrg]
  );

  return (
    <FormControl size="small" sx={{ minWidth: 200, mr: 1 }}>
      <Select
        aria-label="Select organization"
        value={selectedOrg?.org_id ?? ""}
        onChange={handleChange}
        displayEmpty
        sx={{
          backgroundColor: "background.paper",
          borderRadius: "4px",
          height: "36px",
          fontSize: "14px",
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            gap: "4px",
          },
        }}
        startAdornment={
          <BusinessIcon sx={{ color: colors.gold, fontSize: 20, mr: 0.5 }} />
        }
      >
        {options.map((org) => (
          <MenuItem key={org.org_id} value={org.org_id}>
            {displayName(org)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** Organization switcher in the header. Only a JDTech super admin sees it. */
export default function OrganizationSelect() {
  const { signedInUser } = useAuth();

  if (!isSuperAdmin(signedInUser)) {
    return null;
  }

  return <OrganizationSelectForSuperAdmin />;
}
